import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { t, archetypeLabel } from "../lib/i18n";
import AdminBadge from "../components/AdminBadge";
import { ArrowLeft, MapPin, Trophy, Sword, Target } from "@phosphor-icons/react";

export default function PlayerDetail() {
  const { id } = useParams();
  const [player, setPlayer] = useState(null);
  const [decks, setDecks] = useState([]);

  useEffect(() => {
    api.get(`/players/${id}`).then((r) => setPlayer(r.data)).catch(() => {});
    api.get(`/decks`).then((r) => setDecks(r.data.filter((d) => d.owner_player_id === id)));
  }, [id]);

  if (!player)
    return <div className="max-w-[1400px] mx-auto px-6 py-20 font-mono text-white/40">{t.loading}</div>;

  const winRate = ((player.wins / Math.max(1, player.wins + player.losses)) * 100).toFixed(1);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
      <Link
        to="/players"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-white/60 hover:text-white"
      >
        <ArrowLeft size={12} /> {t.back}
      </Link>

      <div className="mt-6 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 border border-white/10 bg-[#0C0C14] p-8 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[#FF0055]/10 blur-3xl" />
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono">
            // {t.bladerProfile}
          </div>
          <h1 className="font-display font-black uppercase italic text-6xl sm:text-7xl leading-none mt-3 flex items-center gap-3 flex-wrap" data-testid="player-handle">
            <span>{player.handle}</span>
            {player.is_admin && <AdminBadge size="md" />}
          </h1>
          <div className="text-white/60 mt-3 flex items-center gap-3 text-sm font-mono">
            <span className="flex items-center gap-1.5"><MapPin size={12} /> {player.country}</span>
            <span>·</span>
            <span>{player.real_name}</span>
          </div>
          <p className="mt-6 text-white/70 max-w-xl">{player.bio}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Stat icon={Trophy} label={t.wins} value={player.wins} accent="cyan" />
          <Stat icon={Sword} label={t.losses} value={player.losses} accent="pink" />
          <Stat icon={Target} label={t.winRate} value={`${winRate}%`} />
          <Stat label={t.points} value={player.points} />
        </div>
      </div>

      <div className="mt-12">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono mb-3">
          // Loadouts
        </div>
        <h2 className="font-display font-black uppercase italic text-3xl mb-6">{t.decksUsed}</h2>
        {decks.length === 0 ? (
          <div className="text-white/40 text-sm font-mono">{t.noDecksLinked}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {decks.map((d) => (
              <Link
                key={d.id}
                to={`/decks/${d.id}`}
                className="border border-white/10 bg-[#0C0C14] p-5 hover:border-[#00E5FF]/50"
              >
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF0055]">
                  {archetypeLabel(d.archetype)}
                </div>
                <div className="font-display font-black uppercase italic text-2xl mt-2">{d.name}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  const color = accent === "cyan" ? "text-[#00E5FF]" : accent === "pink" ? "text-[#FF0055]" : "text-white";
  return (
    <div className="border border-white/10 bg-[#0C0C14] p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div className={`font-display font-black italic text-3xl mt-2 ${color}`}>{value}</div>
    </div>
  );
}
